import { buildScopeTranslate } from "lib/I18n";
import { assessmentCopyKey, isSaqKnowledgeCheck } from "./utils";
import { ATTEMPT_STATUS, QUESTION_TYPES, SAQ_BAND_CONFIG } from "./constants";

const tScoringWait = buildScopeTranslate(
  "views.content.assessment_framework.assessments.pages.scoring_wait"
);

export function scoringWaitCopy() {
  return {
    heading: tScoringWait("heading"),
    body: tScoringWait("body"),
    warning: tScoringWait("warning"),
  };
}

function learnerResponseFor(knowledgeCheck, answer) {
  return isSaqKnowledgeCheck(knowledgeCheck) ? { text: answer } : { selected_choice_ids: [answer] };
}

export function buildSubmitResponses(attempt, selectedAnswers) {
  return (attempt?.knowledge_checks || [])
    .map((knowledgeCheck) => {
      const answer = selectedAnswers[knowledgeCheck.id];

      if (!answer) {
        return null;
      }

      return {
        knowledge_check_id: knowledgeCheck.id,
        response: learnerResponseFor(knowledgeCheck, answer),
      };
    })
    .filter(Boolean);
}

// Result-card shaped responses for an errored attempt. The server's error payload
// has the questions but no learner answers, so the answers come from client state.
export function buildErrorResponses(attempt, selectedAnswers) {
  return (attempt?.knowledge_checks || []).map((knowledgeCheck) => {
    const answer = selectedAnswers?.[knowledgeCheck.id];

    return {
      id: knowledgeCheck.id,
      type: knowledgeCheck.type,
      prompt: knowledgeCheck.prompt,
      choices: knowledgeCheck.body?.choices || [],
      learner_response: answer ? learnerResponseFor(knowledgeCheck, answer) : {},
    };
  });
}

function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function getResultResponses(result) {
  if (Array.isArray(result?.responses)) {
    return result.responses;
  }

  if (Array.isArray(result?.knowledge_check_results)) {
    return result.knowledge_check_results;
  }

  return [];
}

function getAttemptCount(presentation, result) {
  if (result?.attempts_used != null) {
    return toNumber(result.attempts_used);
  }
  return Math.max(
    toNumber(presentation?.learner_context?.attempt_count),
    toNumber(result?.attempt_number)
  );
}

function remainingAttemptCount(maxAttempts, attemptCount) {
  if (maxAttempts == null) {
    return null;
  }

  return Math.max(maxAttempts - attemptCount, 0);
}

function passedSummaryText(summary, translate, testOut) {
  const key = summary.containsSaq ? "summary_passed_saq" : "summary_passed";

  return translate(assessmentCopyKey(key, testOut), {
    correct: summary.correct,
    total: summary.total,
  });
}

function failedSummaryText(summary, translate, testOut) {
  const { containsSaq, correct, total, passingScorePercent, remainingAttempts } = summary;

  if (!containsSaq) {
    return translate(assessmentCopyKey("summary_failed", testOut), {
      correct,
      total,
      passing: passingScorePercent,
    });
  }

  const base = assessmentCopyKey("summary_failed_saq", testOut);
  const key = remainingAttempts == null ? base : `${base}_attempts`;
  const params = { passingScorePercent };

  if (remainingAttempts != null) {
    params.count = remainingAttempts;
  }

  return translate(key, params);
}

export function isGradedResultPayload(result) {
  return Boolean(result && typeof result === "object" && result.status === "graded");
}

export function deriveResultLabels(summary, translate, testOut = null) {
  const { passed, scorePercent, retakeAllowed, status } = summary;

  if (status === ATTEMPT_STATUS.AWAITING_EVALUATION) {
    const { heading, body, warning } = scoringWaitCopy();

    return {
      heading,
      summary: "",
      subtitle: body,
      warning,
      progressLabel: translate("progress_label", { score: scorePercent }),
    };
  }

  const heading = passed
    ? translate("heading_passed", { score: scorePercent })
    : translate("heading_failed", { score: scorePercent });

  const summaryText = passed
    ? passedSummaryText(summary, translate, testOut)
    : failedSummaryText(summary, translate, testOut);

  let subtitle;
  if (passed) {
    subtitle = translate("subtitle_passed");
  } else if (retakeAllowed) {
    subtitle = translate(assessmentCopyKey("subtitle_failed", testOut));
  } else {
    subtitle = translate("subtitle_exhausted");
  }

  const progressLabel = translate("progress_label", { score: scorePercent });

  return { heading, summary: summaryText, subtitle, warning: "", progressLabel };
}

export function getSaqBandConfig(band) {
  return SAQ_BAND_CONFIG[band] || { variant: "warning", labelKey: "grade_unknown" };
}

export function getSaqBandIcon(band) {
  return SAQ_BAND_CONFIG[band]?.variant || "warning";
}

export function choiceLetter(index) {
  return String.fromCharCode(65 + index);
}

// An SAQ on an errored attempt that the grader never reached. One it graded before
// the failure keeps its saq_grade and renders like any graded SAQ.
export function isUngradedSaqError(response, status) {
  return status === ATTEMPT_STATUS.ERROR
    && response.type.toUpperCase() === QUESTION_TYPES.SAQ
    && !response.saq_grade;
}

function saqFeedbackText(response, failed, evaluating, translate) {
  if (failed) {
    return translate("grading_error_feedback");
  }

  return evaluating ? translate("evaluating_response") : response.saq_grade?.feedback ?? "";
}

// How the feedback block of an SAQ card reads for the attempt's status: one the grader never
// reached says so, one still grading says it is being evaluated, otherwise it is the feedback
// (blank when the grader sent none, as for an unanswered question).
// shaded and muted are the grey look; busy is whether grading is still running.
export function getSaqFeedbackState(response, status, translate) {
  const failed = isUngradedSaqError(response, status);
  const grading = status !== ATTEMPT_STATUS.GRADED && status !== ATTEMPT_STATUS.ERROR;
  const evaluating = grading && !response.saq_grade;

  return {
    html: saqFeedbackText(response, failed, evaluating, translate),
    shaded: grading || failed,
    muted: evaluating || failed,
    busy: grading,
  };
}

// An SAQ the grader has finished with, or reached before the attempt errored.
function isSettledSaq(response, status) {
  const settled = status === ATTEMPT_STATUS.GRADED || status === ATTEMPT_STATUS.ERROR;

  return response.type.toUpperCase() === QUESTION_TYPES.SAQ && settled && !isUngradedSaqError(response, status);
}

// The rubric category shown beside a settled SAQ (e.g. "Fully Meets Expectations"); null while
// the SAQ is still grading or when the grader never reached it.
export function getSaqBandLabel(response, status, translate) {
  if (!isSettledSaq(response, status)) {
    return null;
  }

  return translate(getSaqBandConfig(response.saq_grade?.band).labelKey);
}

// The tone (success, warning or error) that colors a graded SAQ's feedback block; undefined while
// the SAQ is still grading, when the grader never reached it, or for a band we don't recognize.
export function getSaqFeedbackTone(response, status) {
  if (!isSettledSaq(response, status)) {
    return undefined;
  }

  return SAQ_BAND_CONFIG[response.saq_grade?.band]?.variant;
}

// A settled SAQ's icon is decorative: the category text beside it carries the meaning, so a
// screen reader does not announce it twice.
export function iconAltText(response, status, iconKey, icon, translate) {
  if (isUngradedSaqError(response, status)) {
    return translate("icon_grading_error");
  }

  if (iconKey === "loading") {
    return translate("icon_loading_feedback");
  }

  if (isSettledSaq(response, status)) {
    return "";
  }

  return translate(icon.altKey);
}

// Null means no badge: an MCQ was graded when it was submitted, but a response built
// from client state (the results fetch failed) does not say whether it was correct.
function mcqIconKey(response) {
  return response.correct ? "success" : "error";
}

function errorIconKey(response, questionType) {
  if (questionType === QUESTION_TYPES.SAQ) {
    return response.saq_grade ? getSaqBandIcon(response.saq_grade.band) : "warning";
  }

  return typeof response.correct === "boolean" ? mcqIconKey(response) : null;
}

export function getResponseIconKey(response, status) {
  const questionType = response.type.toUpperCase();

  if (status === ATTEMPT_STATUS.ERROR) {
    return errorIconKey(response, questionType);
  }

  if (questionType === QUESTION_TYPES.MCQ) {
    return mcqIconKey(response);
  }

  if (questionType === QUESTION_TYPES.SAQ && status === ATTEMPT_STATUS.GRADED) {
    return getSaqBandIcon(response.saq_grade?.band);
  }

  return "loading";
}

export function deriveResultSummary(result, presentation) {
  const responses = getResultResponses(result);
  const passed = result?.passed === true;
  const scorePercent = Math.round(toNumber(result?.score) * 100);
  const correct = responses.length
    ? responses.filter((response) => toNumber(response?.earned_points) > 0).length
    : toNumber(result?.earned_points);
  const total = responses.length || toNumber(result?.total_points);
  const passingScorePercent = Math.round(toNumber(presentation?.passing_score) * 100);
  const maxAttempts = presentation?.max_attempts;
  const attemptCount = getAttemptCount(presentation, result);
  const retakeAllowed = !passed && (maxAttempts == null || attemptCount < maxAttempts);

  return {
    passed,
    scorePercent,
    correct,
    total,
    passingScorePercent,
    attemptCount,
    remainingAttempts: remainingAttemptCount(maxAttempts, attemptCount),
    containsSaq: responses.some(isSaqKnowledgeCheck),
    retakeAllowed,
    status: result?.status,
  };
}
